import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AUCUN_DEFAUT,
  getLetterDefaults,
  saveLetterDefaults,
  type LetterDefaults,
} from '../services/letterDefaults';

/**
 * Profil et type de candidature retenus dans le compte, pour préremplir les écrans de lettre.
 *
 * `defauts` vaut null tant que la lecture n'est pas revenue, puis les valeurs du compte (chacune
 * peut rester null). Une lecture qui échoue donne « rien de retenu » : jamais un écran bloqué.
 * `memoriser` est sans attente et sans erreur visible : retenir un choix ne doit jamais retarder ni
 * faire échouer une génération qui vient d'être décomptée.
 */
export function useLetterDefaults() {
  const [defauts, setDefauts] = useState<LetterDefaults | null>(null);
  const actif = useRef(true);
  // Dernières valeurs connues, lisibles depuis `memoriser` sans le recréer à chaque lecture.
  const connus = useRef<LetterDefaults | null>(null);
  // Ce qui a été écrit avec succès pendant cette vie du hook : une lecture qui arrive APRÈS une
  // écriture reflète un état antérieur, elle ne doit pas le remettre à l'écran ni l'effacer.
  const ecrits = useRef<Partial<LetterDefaults>>({});

  useEffect(() => {
    actif.current = true;
    getLetterDefaults()
      .catch(() => AUCUN_DEFAUT)
      .then((lu) => {
        if (!actif.current) return;
        const d = { ...lu, ...ecrits.current };
        connus.current = d;
        setDefauts(d);
      });
    return () => {
      actif.current = false;
    };
  }, []);

  const memoriser = useCallback((patch: Partial<LetterDefaults>) => {
    // Rien à écrire si le compte a déjà ces valeurs : pas d'aller-retour réseau à chaque lettre.
    const deja = connus.current;
    const aEcrire: Partial<LetterDefaults> = {};
    if (patch.profil && patch.profil !== deja?.profil) aEcrire.profil = patch.profil;
    if (patch.type && patch.type !== deja?.type) aEcrire.type = patch.type;
    if (!aEcrire.profil && !aEcrire.type) return;

    saveLetterDefaults(aEcrire)
      .then(() => {
        ecrits.current = { ...ecrits.current, ...aEcrire };
        const suivant = { ...(connus.current ?? AUCUN_DEFAUT), ...aEcrire };
        connus.current = suivant;
        if (actif.current) setDefauts(suivant);
      })
      .catch(() => undefined);
  }, []);

  return { defauts, memoriser };
}
